import React, { useEffect } from 'lib/react';
import {
  EVGrid, EVPaper, EVAlert, EVTypography, EVTextField, EVButton, EVSkeleton,
} from 'lib/components';
import { useTranslation } from 'lib/translation';
import { ORDER_HISTORY_ROUTE, SUPPORT_ROUTE } from 'constants/RouteConstants';
import UploadBlueprint from 'commonComponents/uploadBlueprint';
import { useDispatch, useSelector } from 'lib/redux';
import history from 'store/CreateHistory';
import {
  UPLOAD_INFORMATION_UPLOAD_BLUEPRINT_FILE, UPLOAD_INFORMATION_UPDATE_INSTRUCTIONS, CLEAR_UPLOAD_INFORMATION_DATA, LOADING_UPLOAD_INFORMATION_PAGE,
  UPLOAD_INFORMATION_UPLOAD_BLUEPRINT_FILE_SUCCESS,
  UPLOAD_INFORMATION_UPLOAD_BLUEPRINT_FILE_ERROR,
} from 'constants/ActionTypes';
import { openCancelOrderDialog } from 'features/cancel-order-dialog/CancelOrder.actions';
import RetryScreen from 'commonComponents/retry-screen';
import saveUploadInformationService from 'services/saveUploadInformation.service';
import { openOrderHistoryAlert } from 'features/orderHistory/OrderHistory.actions';
import { SUCCESS } from 'constants/AppConstants';
import { scrollToTop } from 'utils/app.utils';
import getResourceFileService from 'services/getResourceFile.service';
import Banner from 'commonComponents/banner';
import { SUB_STATUSES } from 'features/orderHistory/OrderHistory.constants';
import OrderResolvedScreen from 'commonComponents/orderResolvedScreen';
import { useLocation } from 'lib/router';
import { isAuthorized } from 'utils/auth.utils';
import { loadUploadInformationPage, saveUploadInformationError, saveUploadInformationSuccess } from './UploadInformation.actions';
import UploadInformationStyles from './UploadInformationStyles';

function UploadInformation() {
  const { t } = useTranslation();
  const classes = UploadInformationStyles();
  const dispatch = useDispatch();
  const location = useLocation();
  const currentUrl = location.state?.currentUrl;
  const backUrl = currentUrl?.includes(ORDER_HISTORY_ROUTE) ? currentUrl : ORDER_HISTORY_ROUTE;

  const {
    reportId, reportDetails, isLoading, loadError, bluePrintFileId,
    uploadFileAlert, bluePrintFileData, bluePrintFileName, uploadBluePrintFileError, uploadingBluePrintFile,
  } = useSelector(({ uploadInformation, nonPersisted: { uploadInformationBlueprintFileData } }) => ({
    ...uploadInformation,
    bluePrintFileData: uploadInformationBlueprintFileData,
  }));

  useEffect(() => {
    dispatch(loadUploadInformationPage());
    return () => dispatch({ type: CLEAR_UPLOAD_INFORMATION_DATA });
  }, []);

  useEffect(() => {
    if (bluePrintFileId && !bluePrintFileData) {
      dispatch({ type: LOADING_UPLOAD_INFORMATION_PAGE });

      getResourceFileService(bluePrintFileId).then((response) => {
        if (response.ok) {
          const { fileData } = response.json;
          dispatch({ type: UPLOAD_INFORMATION_UPLOAD_BLUEPRINT_FILE_SUCCESS, fileData });
        } else dispatch({ type: UPLOAD_INFORMATION_UPLOAD_BLUEPRINT_FILE_ERROR, error: 'Internal Server Error' });
      });
    }
  }, [bluePrintFileId, bluePrintFileData]);

  const handleInstructionsChange = (e) => {
    dispatch({ type: UPLOAD_INFORMATION_UPDATE_INSTRUCTIONS, instructions: e.target.value });
  };
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    dispatch({ type: UPLOAD_INFORMATION_UPLOAD_BLUEPRINT_FILE, file });
  };
  const handleCancelReport = () => {
    const mapPoint = { building: reportDetails.building, address: reportDetails.reportAddress };
    dispatch(openCancelOrderDialog(reportId, mapPoint));
  };

  const handleSaveUploadInformation = () => {
    const requestBody = {
      comments: reportDetails.comments,
      sharedFileId: `${bluePrintFileId}`,
    };
    saveUploadInformationService(reportId, requestBody)
      .then(({ ok }) => {
        if (ok) {
          const message = t('upload_information__upload_save_success_message', { reportId });
          if (isAuthorized()) {
            dispatch(openOrderHistoryAlert({
              severity: SUCCESS,
              message,
            }));
            history.push(ORDER_HISTORY_ROUTE);
          } else dispatch(saveUploadInformationSuccess(message));
        } else {
          dispatch(saveUploadInformationError());
        }
        scrollToTop();
      });
  };

  if (loadError) {
    return <RetryScreen handleRetryButtonClick={() => dispatch(loadUploadInformationPage())} />;
  }

  if (Number(reportDetails?.subStatusID) === SUB_STATUSES.BlueprintVerified) {
    return <OrderResolvedScreen backToOrderHistoryUrl={backUrl} />;
  }

  return (
    <>
      {!isLoading && uploadFileAlert?.open && (
        <Banner
          severity={uploadFileAlert.severity}
          message={uploadFileAlert.message}
        />
      )}
      <EVGrid className={classes.uploadInfoContainer}>
        <EVPaper
          elevation={0}
          className={classes.uploadInfoContainerPaper}
        >
          <EVGrid
            container
            className={classes.uploadInfoContent}
          >
            <EVGrid item style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {isLoading
                ? <EVSkeleton style={{ height: '120px', borderRadius: '6px' }} animation="wave" variant="rect" /> : (
                  <EVAlert severity="warning" className={classes.alertContainer}>
                    <EVTypography style={{
                      color: '#1B202C', fontSize: '16px', fontWeight: 400,
                    }}
                    >
                      {t('upload_information__missing_info_alert_text')}
                      <strong>
                        {reportDetails?.missingInformation}
                      </strong>
                      .
                      <br />
                      {t('upload_information__alert_note')}
                      “
                      <strong>{ reportDetails?.fileName }</strong>
                      ”.
                      <br />
                      {t('upload_information__alert_note_2')}
                    </EVTypography>
                  </EVAlert>
                )}
              <EVGrid item>
                {isLoading ? <EVSkeleton style={{ height: '40px', borderRadius: '6px' }} animation="wave" variant="rect" /> : (
                  <EVGrid className={classes.blueprintInfoBox}>
                    <EVGrid style={{ display: 'flex', gap: '16px', alignItems: 'baseline' }}>
                      <EVTypography style={{ textTransform: 'uppercase', fontSize: '12px', fontWeight: 600 }}>
                        {t('blueprint_order__blueprint_id')}
                      </EVTypography>
                      <EVTypography style={{ fontSize: '14px' }}>
                        {reportDetails?.building}
                      </EVTypography>
                    </EVGrid>
                    <EVGrid style={{ display: 'flex', gap: '16px', alignItems: 'baseline' }}>
                      <EVTypography style={{ textTransform: 'uppercase', fontSize: '12px', fontWeight: 600 }}>
                        {t('upload_information__report_number')}
                      </EVTypography>
                      <EVTypography style={{ fontSize: '14px' }}>
                        {reportId}
                      </EVTypography>
                    </EVGrid>
                    <EVGrid style={{ display: 'flex', gap: '16px', alignItems: 'baseline' }}>
                      <EVTypography style={{ textTransform: 'uppercase', fontSize: '12px', fontWeight: 600 }}>
                        {t('pitch_editor__report_type')}
                      </EVTypography>
                      <EVTypography style={{ fontSize: '14px' }}>
                        {reportDetails?.reportType}
                      </EVTypography>
                    </EVGrid>
                  </EVGrid>
                )}
              </EVGrid>
              <EVGrid className={classes.uploadBlueprintContainer}>
                <UploadBlueprint
                  fileData={bluePrintFileData}
                  fileName={bluePrintFileName}
                  uploadError={uploadBluePrintFileError}
                  onFileUpload={handleFileUpload}
                  isUploading={uploadingBluePrintFile}
                  isPageLoading={isLoading}
                />
              </EVGrid>
            </EVGrid>
            <EVGrid style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <EVGrid style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <EVTypography style={{ fontSize: '18px', lineHeight: '27px', fontWeight: '600' }}>
                  {t('upload_information__update_instructions')}
                </EVTypography>
                <EVTypography style={{ fontSize: '16px', lineHeight: '24px' }}>
                  {t('upload_information__update_instructions_additional_text')}
                </EVTypography>
              </EVGrid>
              {isLoading ? <EVSkeleton style={{ height: '120px', borderRadius: '6px' }} animation="wave" variant="rect" /> : (
                <EVTextField
                  id="comments"
                  multiline
                  variant="outlined"
                  fullWidth
                  onChange={handleInstructionsChange}
                  value={reportDetails?.comments}
                  minRows={3}
                  classes={{ root: classes.textarea }}
                />
              )}
            </EVGrid>
            <EVButton
              id="upload-info-save-btn"
              disabled={!bluePrintFileId}
              variant="contained"
              color="primary"
              type="submit"
              onClick={handleSaveUploadInformation}
              className={classes.submitButton}
            >
              {t('common_submit')}
            </EVButton>
            <EVGrid className={classes.redirectionLinksContainer}>
              <EVTypography className={classes.redirectionLinkText}>
                {t('upload_information__need_help')}
                {' '}
                <EVButton onClick={() => history.push(SUPPORT_ROUTE)} className={classes.redirectionButtonStyles}>
                  {t('address_verification__map__customer_service')}
                </EVButton>
              </EVTypography>
              {(isAuthorized()) && (
              <EVTypography className={classes.redirectionLinkText}>
                {t('upload_information__no_longer_want_to_proceed')}
                {' '}
                <EVButton
                  data-testid="upload-info-cancel-order-btn"
                  onClick={() => handleCancelReport()}
                  className={classes.redirectionButtonStyles}
                >
                  {t('upload_information__cancel_this_order')}
                </EVButton>
                {' '}
                {t('upload_information__now')}
              </EVTypography>
              )}
            </EVGrid>
          </EVGrid>
        </EVPaper>
      </EVGrid>
    </>
  );
}

export default UploadInformation;
